<template>
    <comp-model ref="modal" :title="'价格设置 - ' + companyName" width="80%">
        <template #head-operate>
            <div class="action-right">
                <div class="search-wrap">
                    <Input v-model="searchKeyword" class="search-input" placeholder="搜索商品名称" />
                    <span v-if="searchKeyword" class="search-clear" @click="searchKeyword = ''">
                        <Icon type="md-close-circle" />
                    </span>
                </div>
                <button class="btn-add-all" type="button" @click="addAllProducts">一键添加全部</button>
            </div>
        </template>
        <div class="elem-price-setting" dark-class="elem-price-setting-dark">
            <div v-if="items.length" class="eps-list">
                <div class="eps-card" v-for="row in filteredItemRows" :key="row.index">
                    <div class="eps-card-left">
                        <div class="field">
                            <div class="label">商品</div>
                            <Select v-model="row.item.goodsUuid" filterable placeholder="选择商品" @on-change="val => onProductChange(row.index, val)">
                                <Option v-for="p in productOptions" :key="p.uuid" :value="p.uuid">{{ p.title }}</Option>
                            </Select>
                        </div>
                        <div class="meta">
                            <span class="pill" v-if="row.item.productTitle">{{ row.item.productTitle }}</span>
                            <span class="pill light">规格 {{ row.item.specifications.length }}</span>
                        </div>
                        <Button type="text" icon="md-trash" class="remove" @click="removeProduct(row.index)">移除</Button>
                    </div>

                    <div class="eps-card-right">
                        <div class="panel-row">
                            <div class="panel price-panel">
                                <div class="panel-header">
                                    <div class="panel-title">商品价格</div>
                                    <div class="panel-sub">单位：元</div>
                                </div>
                                <div class="panel-body">
                                    <Input v-model.number="row.item.price" type="number" :placeholder="placeholderText || '0.00'" />
                                </div>
                            </div>

                            <div class="panel spec-panel">
                                <div class="panel-header">
                                    <div class="panel-title">规格加价</div>
                                    <div class="panel-sub">为每个规格设置加价</div>
                                </div>
                                <div class="panel-body">
                                    <div class="spec-table" v-if="row.item.specifications.length">
                                        <div class="spec-row head">
                                            <div class="col name">规格名称</div>
                                            <div class="col price">加价(元)</div>
                                        </div>
                                        <div class="spec-row" v-for="(spec, sIndex) in row.item.specifications" :key="sIndex">
                                            <div class="col name readonly">{{ spec.title || "—" }}</div>
                                            <div class="col price">
                                                <Input v-model.number="spec.price" type="number" placeholder="0.00" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="spec-empty" v-else>该商品暂无规格</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="eps-add-row">
                    <Button class="add-chip" type="primary" icon="md-add" shape="circle" @click="addProduct">添加商品</Button>
                </div>
            </div>

            <div v-else class="eps-empty" @click="addProduct">
                <div class="dot"></div>
                <div class="title">暂无商品</div>
                <div class="desc">点击添加商品，配置价格与规格</div>
            </div>
        </div>

        <template #operate="{ close }">
            <div class="operate-box">
                <Button class="btn-cancel" ghost @click="close">
                    <Icon type="md-arrow-back" />
                    取消
                </Button>
                <Button class="btn-save" type="primary" icon="md-checkmark" @click="onSave">保存</Button>
            </div>
        </template>
    </comp-model>
</template>

<script lang="ts">
import Request from "@/module/request/request"
import { PropType } from "vue"
import compModel from './comp-model.vue'
import Message from "@/module/interactive/message"

interface SpecPrice {
    specificationUuid?: string | number
    title?: string
    price: number
}

interface ProductPrice {
    goodsUuid?: string | number | null
    productTitle?: string
    price: number
    specifications: SpecPrice[]
}

interface ProductSpecOption {
    uuid: string | number
    title: string
    price?: number | string
}

interface ProductOption {
    uuid: string | number
    title: string
    price?: number | string
    specifications?: ProductSpecOption[]
}

export default {
  components: { compModel },
    name: "ElemPriceSetting",
    props: {
        name: String,
        value: {
            type: Array as PropType<ProductPrice[]>,
            default: () => [],
        },
        products: {
            type: Array as PropType<ProductOption[]>,
            default: () => [],
        },
        required: {
            type: Boolean,
            default: false,
        },
        placeholder: String,
    },
    emits: ["change", "update:value", "save"],
    data() {
        return {
            items: [] as ProductPrice[],
            fetchedProducts: [] as ProductOption[],
            isInner: false,
            companyName: "",
            companyUuid: "",
            searchKeyword: "",
        }
    },
    computed: {
        productMap(): Map<string, ProductOption> {
            const map = new Map<string, ProductOption>()
            this.productOptions.forEach((p: ProductOption) => map.set(String(p.uuid), p))
            return map
        },
        productOptions(): ProductOption[] {
            return (this.products && this.products.length ? this.products : this.fetchedProducts) || []
        },
        placeholderText(): string {
            return this.placeholder || ""
        },
        filteredItemRows(): { item: ProductPrice; index: number }[] {
            const keyword = (this.searchKeyword || "").trim().toLowerCase()
            const rows = this.items.map((item, index) => ({ item, index }))
            if (!keyword) return rows

            return rows.filter(r => {
                const title = (r.item.productTitle || "").toLowerCase()
                const optionTitle = (this.getProductOption(r.item.goodsUuid)?.title || "").toLowerCase()
                return title.includes(keyword) || optionTitle.includes(keyword)
            })
        },
    },
    watch: {
        value: {
            handler(val: ProductPrice[]) {
                if (this.isInner) {
                    this.isInner = false
                    return
                }
                this.items = this.normalizeIn(val)
            },
            immediate: true,
            deep: true,
        },
        productOptions: {
            handler() {
                this.syncItemsFromOptions()
            },
            deep: true,
        },
        items: {
            handler() {
                this.emitChange()
            },
            deep: true,
        },
    },
    methods: {
        productKey(v: any): string {
            return v === undefined || v === null ? "" : String(v)
        },
        getProductOption(goodsUuid: any): ProductOption | undefined {
            const key = this.productKey(goodsUuid)
            if (!key) return undefined
            return this.productMap.get(key)
        },
        async display(companyUuid: string, companyName?: string) {
            this.companyUuid = companyUuid
            this.companyName = companyName || ""
            this.fetchedProducts = await Request.get("ADMIN://Goods/FindAllOptions")
            const res = await Request.get("ADMIN://CompanyPrice/FindAllByCompanyUuid", {
                companyUuid: companyUuid,
            })
            this.items = this.normalizeIn(res)
            this.syncItemsFromOptions()
            this.$refs.modal.display()
        },
        
        priceIn(raw: number | string | undefined | null): number {
            const num = Number(raw)
            if (Number.isNaN(num)) return 0
            return Number((num / 100).toFixed(2))
        },
        priceOut(raw: number | string | undefined | null): number {
            const num = Number(raw)
            if (Number.isNaN(num)) return 0
            return Math.round(num * 100)
        },
        findSpecTitle(goodsUuid: string | number | undefined | null, specUuid: string | number | undefined | null, fallback?: string): string {
            if (!goodsUuid || !specUuid) return fallback || ""
            const option = this.getProductOption(goodsUuid)
            const spec = option?.specifications?.find(s => String(s.uuid) === String(specUuid))
            return spec?.title || fallback || ""
        },
        buildSpecs(goodsUuid: string | number | null, rawSpecs: any[]): SpecPrice[] {
            const option = this.getProductOption(goodsUuid)
            const optionSpecs = option?.specifications || []
            if (rawSpecs && rawSpecs.length) {
                return rawSpecs.map((s: any) => {
                    const specificationUuid = s.specificationUuid ?? s.uuid ?? null
                    const matched = optionSpecs.find(os => String(os.uuid) === String(specificationUuid))
                    return {
                        specificationUuid,
                        title: matched?.title || this.findSpecTitle(goodsUuid, specificationUuid, s.title || ""),
                        price: this.priceIn(s.price),
                    }
                })
            }

            // 如果接口未返回规格明细，根据商品规格生成空的价格项
            return optionSpecs.map(os => ({
                specificationUuid: os.uuid,
                title: os.title,
                price: this.priceIn((os as any).price),
            }))
        },
        createItemFromOption(option: ProductOption): ProductPrice {
            return {
                goodsUuid: option.uuid,
                productTitle: option.title,
                price: this.priceIn((option as any).price),
                specifications: (option.specifications || []).map(s => ({
                    specificationUuid: s.uuid,
                    title: s.title,
                    price: this.priceIn((s as any).price),
                })),
            }
        },
        syncItemsFromOptions() {
            if (!this.items || !this.items.length) return
            this.items = this.items.map(item => {
                if (!item.goodsUuid) return item
                const option = this.getProductOption(item.goodsUuid)
                if (!option) return item

                const existedPriceMap = new Map<string, number>()
                item.specifications.forEach(s => {
                    const key = String(s.specificationUuid ?? s.title ?? "")
                    existedPriceMap.set(key, s.price)
                })

                const specs = (option.specifications || []).map(spec => {
                    const key = String(spec.uuid)
                    const existed = existedPriceMap.get(key)
                    return {
                        specificationUuid: spec.uuid,
                        title: spec.title,
                        price: typeof existed === "number" ? existed : this.priceIn((spec as any).price),
                    }
                })

                return {
                    ...item,
                    productTitle: option.title,
                    specifications: specs,
                }
            })
        },
        normalizeIn(val?: ProductPrice[] | null): ProductPrice[] {
            if (!val || !Array.isArray(val)) return []
            return val.map(v => {
                const goodsUuid = (v as any).goodsUuid ?? (v as any).productId ?? (v as any).goods?.uuid ?? null
                const specs = (v as any).specs || v.specifications || []
                return {
                    goodsUuid,
                    productTitle: v.productTitle || this.getProductOption(goodsUuid)?.title || "",
                    price: this.priceIn((v as any).price),
                    specifications: this.buildSpecs(goodsUuid, specs),
                }
            })
        },
        normalizeOut(): ProductPrice[] {
            return this.items.map(v => ({
                companyUuid: this.companyUuid || "",
                goodsUuid: v.goodsUuid ?? null,
                productTitle: v.productTitle || this.getProductOption(v.goodsUuid)?.title || "",
                price: this.priceOut(v.price),
                specs: v.specifications.map(s => ({
                    specificationUuid: s.specificationUuid ?? null,
                    price: this.priceOut(s.price),
                })),
            }))
        },
        emitChange() {
            const payload = this.normalizeOut()
            this.isInner = true
            this.$emit("update:value", payload)
            this.$emit("change", {
                name: this.name,
                value: payload,
                type: "elem-price-setting",
            })
        },
        onSave() {
            const payload = this.normalizeOut()
            Request.post("ADMIN://CompanyPrice/SaveBatch", {
                companyPrices: payload,
                companyUuid: this.companyUuid,
            }, {
                json: true,
            }).then(() => {
                Message.success("保存成功")
                this.$refs.modal.close()
            })
        },
        addProduct() {
            this.items.push({
                goodsUuid: null,
                productTitle: "",
                price: 0,
                specifications: [],
            })
        },
        addAllProducts() {
            const source = (this.fetchedProducts && this.fetchedProducts.length ? this.fetchedProducts : this.productOptions) || []
            if (!source.length) {
                Message.info("暂无可添加商品")
                return
            }

            const existed = new Set<string>()
            this.items.forEach(i => {
                if (i.goodsUuid !== undefined && i.goodsUuid !== null && String(i.goodsUuid).length) {
                    existed.add(String(i.goodsUuid))
                }
            })

            const toAdd = source.filter(p => !existed.has(String(p.uuid)))
            if (!toAdd.length) {
                Message.info("所有商品都已添加")
                return
            }

            toAdd.forEach(p => {
                this.items.push(this.createItemFromOption(p))
            })
        },
        removeProduct(index: number) {
            this.items.splice(index, 1)
        },
        onProductChange(index: number, val: string | number) {
            const option = this.getProductOption(val)
            const target = this.items[index]
            target.goodsUuid = val
            target.productTitle = option ? option.title : ""

            // 选择商品后：默认带出 fetchedProducts(商品选项) 的商品价格与规格加价
            if (option) {
                target.price = this.priceIn((option as any).price)

                if (option.specifications && option.specifications.length) {
                    const existedPriceMap = new Map<string | number, number>()
                    target.specifications.forEach(s => {
                        const key = s.specificationUuid ?? s.title ?? ""
                        existedPriceMap.set(key as any, s.price)
                    })

                    target.specifications = option.specifications.map(spec => {
                        const existed = existedPriceMap.get(spec.uuid)
                        return {
                            specificationUuid: spec.uuid,
                            title: spec.title,
                            price: typeof existed === "number" ? existed : this.priceIn((spec as any).price),
                        }
                    })
                } else {
                    target.specifications = []
                }
            } else {
                target.price = 0
                target.specifications = []
            }
        },
    },
}
</script>

<style lang="less" scoped>
@import (reference) "/src/style/color.less";
@import (reference) "/src/style/utils.less";

.action-right {
    display: flex;
    align-items: center;
    gap: 10px;

    .search-wrap {
        position: relative;
        display: inline-flex;
        align-items: center;
    }

    .search-input {
        width: 260px;
        max-width: 40vw;
    }

    .search-wrap :deep(.ivu-input) {
        padding-right: 30px;
    }

    .search-clear {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: #9ca3af;
        cursor: pointer;
        line-height: 1;
    }

    .search-clear:hover {
        color: #6b7280;
    }

    .btn-add-all {
        appearance: none;
        border: 1px solid #cbd5e1;
        background: #fff;
        color: #374151;
        padding: 6px 12px;
        border-radius: 8px;
        font-size: 13px;
        cursor: pointer;
    }

    .btn-add-all:hover {
        background: #f3f4f6;
    }
}

.elem-price-setting {
    padding: 30px;
    width: 100%;
    box-sizing: border-box;

    .eps-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .eps-add-row {
        display: flex;
        justify-content: center;
        padding: 10px 0 4px;

        .add-chip {
            border-radius: 18px;
            box-shadow: 0 10px 20px rgba(59, 130, 246, 0.18);
            padding: 0 18px;

            :deep(i)::before {
                color: #fff;
            }
        }
    }

    .eps-card {
        display: grid;
        grid-template-columns: 260px 1fr;
        gap: 16px;
        padding: 16px;
        border-radius: 12px;
        background: linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
        border: 1px solid #e8ecf5;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
        position: relative;
        overflow-x: auto;

        &:hover {
            box-shadow: 0 14px 40px rgba(0, 0, 0, 0.06);
            border-color: #d5ddf0;
        }
    }

    .eps-card-left {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding-right: 10px;
        border-right: 1px solid #eef1f7;

        .field {
            .label {
                font-size: 13px;
                color: #6b7280;
                margin-bottom: 6px;
            }
        }

        .meta {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;

            .pill {
                display: inline-flex;
                align-items: center;
                padding: 4px 10px;
                border-radius: 12px;
                background: #1f2937;
                color: #fff;
                font-size: 12px;
                letter-spacing: 0.3px;

                &.light {
                    background: #eef2f7;
                    color: #4b5563;
                }
            }
        }

        .remove {
            align-self: flex-start;
            color: #c53030;
            padding: 0;
        }
    }

    .eps-card-right {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .panel-row {
            display: grid;
            grid-template-columns: 260px 1fr;
            gap: 12px;
            align-items: start;

            @media (max-width: 960px) {
                grid-template-columns: 1fr;
            }
        }

        .panel {
            border: 1px solid #edf0f7;
            border-radius: 10px;
            background: #fff;
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);

            .panel-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 10px 12px;
                border-bottom: 1px dashed #e5e7eb;
                color: #4a5568;

                .panel-title {
                    font-weight: 700;
                    font-size: 14px;
                }

                .panel-sub {
                    font-size: 12px;
                    color: #9ca3af;
                }
            }

            .panel-body {
                padding: 12px;
            }
        }

        .price-panel {
            min-width: 220px;
            max-width: 280px;
        }

        .spec-panel {
            width: 100%;
        }

        .spec-table {
            width: 100%;

            .spec-row {
                display: grid;
                grid-template-columns: 1fr 160px;
                gap: 8px;
                align-items: center;
                padding: 8px 0;
                border-bottom: 1px dashed #e5e7eb;

                &.head {
                    color: #6b7280;
                    font-weight: 600;
                }

                &:last-child {
                    border-bottom: none;
                }

                .col.name.readonly {
                    padding: 0 8px;
                    color: #111827;
                    font-weight: 600;
                }

                .col.price input {
                    border-radius: 8px;
                }
            }
        }

        .spec-empty {
            font-size: 12px;
            color: #9ca3af;
        }
    }

    .eps-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 40px 20px;
        border: 1px dashed #d5ddf0;
        border-radius: 12px;
        background: #fbfcfe;
        color: #4b5563;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
            border-color: #c0c9e0;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
        }

        .dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: linear-gradient(135deg, #3b82f6, #8b5cf6);
            margin-bottom: 10px;
        }

        .title {
            font-size: 15px;
            font-weight: 600;
        }

        .desc {
            font-size: 12px;
            color: #6b7280;
            margin-top: 4px;
        }
    }
}

.elem-price-setting-dark {
    :deep(.ivu-input) {
        background: #1c1f25;
        border-color: #2f3440;
        color: #e5e7eb;
    }

    :deep(.ivu-input:hover) {
        border-color: #3b4252;
    }

    :deep(.ivu-input:focus) {
        border-color: #3b4252;
        box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
    }

    :deep(.ivu-input::placeholder) {
        color: #9ca3af;
    }

    :deep(.ivu-select-selection) {
        background: #1c1f25;
        border-color: #2f3440;
    }

    :deep(.ivu-select-selection:hover) {
        border-color: #3b4252;
    }

    :deep(.ivu-select-selected-value),
    :deep(.ivu-select-placeholder),
    :deep(.ivu-select-arrow) {
        color: #e5e7eb;
    }

    :deep(.ivu-select-dropdown) {
        background: #1c1f25;
        border-color: #2f3440;
    }

    :deep(.ivu-select-item) {
        color: #e5e7eb;
    }

    :deep(.ivu-select-item:hover) {
        background: #2a2f3a;
    }

    .eps-actions {
        border-color: #2f3440;
        background: linear-gradient(135deg, #23262d 0%, #1c1f25 100%);

        .title {
            color: #e5e7eb;
        }

        .btn-add-all {
            border-color: #3b4252;
            background: #1c1f25;
            color: #e5e7eb;
        }

        .btn-add-all:hover {
            background: #2a2f3a;
        }
    }

    .eps-card {
        background: linear-gradient(135deg, #23262d 0%, #1c1f25 100%);
        border-color: #2f3440;
    }

    .eps-card-left {
        border-right-color: #2f3440;

        .label {
            color: #d1d5db;
        }

        .meta .pill.light {
            background: #2f3440;
            color: #e5e7eb;
        }
    }

    .eps-card-right {
        .base-price .label {
            color: #e5e7eb;
        }

        .panel {
            background: #1c1f25;
            border-color: #2f3440;

            .panel-header {
                border-bottom-color: #2f3440;
                color: #e5e7eb;

                .panel-sub {
                    color: #9ca3af;
                }
            }
        }

        .spec-table .spec-row {
            border-bottom-color: #2f3440;

            .col.name.readonly {
                color: #e5e7eb;
            }
        }

        .spec-empty {
            color: #9ca3af;
        }
    }

    .eps-empty {
        background: #1c1f25;
        border-color: #2f3440;
        color: #e5e7eb;

        .desc {
            color: #9ca3af;
        }
    }
}

:deep(.operate-box) {
    padding: 14px 24px 18px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    background: linear-gradient(135deg, #f9fafc 0%, #f1f5fb 100%);
    border-top: 1px solid #e8ecf5;

    .btn-cancel {
        min-width: 100px;
        border-radius: 24px;
        color: #4b5563;
        border-color: #d2d8e3;
        background: #fff;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    .btn-save {
        min-width: 110px;
        border-radius: 24px;
        background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%);
        border: none;
        box-shadow: 0 12px 24px rgba(59, 130, 246, 0.2);
    
        i::before {
            color: #fff;
        }
    }
}

:deep(.content-dark .operate-box) {
    background: linear-gradient(135deg, #1f232b 0%, #171a1f 100%);
    border-top-color: #2f3440;

    .btn-cancel {
        color: #e5e7eb;
        border-color: #3b4252;
        background: #1c1f25;
        box-shadow: none;
    }

    .btn-save {
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
    }
}
</style>
